import { Link, Outlet, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useState } from "react";

export function Layout({ admin = false }: { admin?: boolean }) {
  const { itemCount } = useCart();
  const { logout, username } = useAuth();
  const { customer, isAuthenticated, logout: customerLogout } = useCustomerAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const location = useLocation();

  if (admin) {
    // Admin layout
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-black text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/admin" className="text-xl font-bold">
                Deb's Expressions Admin
              </Link>
              <nav className="hidden md:flex items-center gap-6">
                <Link
                  to="/admin"
                  className={`hover:text-gray-300 ${
                    location.pathname === "/admin" ? "underline" : ""
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/admin/products"
                  className={`hover:text-gray-300 ${
                    location.pathname === "/admin/products" ? "underline" : ""
                  }`}
                >
                  Products
                </Link>
                <Link
                  to="/admin/orders"
                  className={`hover:text-gray-300 ${
                    location.pathname === "/admin/orders" ? "underline" : ""
                  }`}
                >
                  Orders
                </Link>
                <Link
                  to="/admin/users"
                  className={`hover:text-gray-300 ${
                    location.pathname === "/admin/users" ? "underline" : ""
                  }`}
                >
                  Users
                </Link>
                <Link
                  to="/admin/contact-messages"
                  className={`hover:text-gray-300 ${
                    location.pathname === "/admin/contact-messages" ? "underline" : ""
                  }`}
                >
                  Messages
                </Link>
                <Link
                  to="/admin/settings"
                  className={`hover:text-gray-300 ${
                    location.pathname === "/admin/settings" ? "underline" : ""
                  }`}
                >
                  Settings
                </Link>
                <span className="text-gray-400">|</span>
                <span className="text-sm">{username}</span>
                <Link
                  to="/admin/account"
                  className="text-sm hover:text-gray-300"
                >
                  Account
                </Link>
                <button
                  onClick={() => logout()}
                  className="text-sm hover:text-gray-300"
                >
                  Logout
                </button>
                <Link to="/" className="text-sm hover:text-gray-300">
                  View Store
                </Link>
              </nav>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Outlet />
        </main>
      </div>
    );
  }

  // Public layout
  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold">
              Deb's Expressions
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            {/* Desktop navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="hover:text-gray-600">
                Home
              </Link>
              <Link to="/products/wind-spinners" className="hover:text-gray-600">
                Wind Spinners
              </Link>
              <Link to="/products/glassware" className="hover:text-gray-600">
                Glassware
              </Link>
              <Link to="/products/tumblers" className="hover:text-gray-600">
                Tumblers
              </Link>
              <Link to="/products/in-memory-of" className="hover:text-gray-600">
                In Memory Of
              </Link>
              <Link to="/contact" className="hover:text-gray-600">
                Contact Us
              </Link>

              {/* Customer Account Dropdown */}
              {isAuthenticated && customer ? (
                <div className="relative">
                  <button
                    onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                    className="flex items-center gap-2 hover:text-gray-600"
                  >
                    {customer.avatarUrl ? (
                      <img
                        src={customer.avatarUrl}
                        alt={customer.firstName}
                        className="w-6 h-6 rounded-full"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-xs font-bold text-gray-600">
                          {customer.firstName.charAt(0)}
                        </span>
                      </div>
                    )}
                    <span>{customer.firstName}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {accountMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <Link
                        to="/account"
                        className="block px-4 py-2 hover:bg-gray-50"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                      <Link
                        to="/account/profile"
                        className="block px-4 py-2 hover:bg-gray-50"
                        onClick={() => setAccountMenuOpen(false)}
                      >
                        Profile
                      </Link>
                      <hr className="my-2" />
                      <button
                        onClick={async () => {
                          await customerLogout();
                          setAccountMenuOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-50"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className="hover:text-gray-600">
                  Login
                </Link>
              )}

              <Link to="/cart" className="relative hover:text-gray-600">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-black text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                    {itemCount}
                  </span>
                )}
              </Link>
            </nav>
          </div>

          {/* Mobile navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden py-4 space-y-2">
              <Link
                to="/"
                className="block py-2 hover:text-gray-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/products/wind-spinners"
                className="block py-2 hover:text-gray-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Wind Spinners
              </Link>
              <Link
                to="/products/glassware"
                className="block py-2 hover:text-gray-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Glassware
              </Link>
              <Link
                to="/products/tumblers"
                className="block py-2 hover:text-gray-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Tumblers
              </Link>
              <Link
                to="/products/in-memory-of"
                className="block py-2 hover:text-gray-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                In Memory Of
              </Link>
              <Link
                to="/contact"
                className="block py-2 hover:text-gray-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact Us
              </Link>
              {isAuthenticated && customer ? (
                <>
                  <Link
                    to="/account"
                    className="block py-2 hover:text-gray-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Account
                  </Link>
                  <button
                    onClick={async () => {
                      await customerLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left py-2 hover:text-gray-600"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="block py-2 hover:text-gray-600"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
              )}
              <Link
                to="/cart"
                className="block py-2 hover:text-gray-600"
                onClick={() => setMobileMenuOpen(false)}
              >
                Cart ({itemCount})
              </Link>
            </nav>
          )}
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-black text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="mb-2">
              Questions or custom bulk orders? Email us at{" "}
              <a
                href="mailto:DEBSEXPRESSIONS@GMAIL.COM"
                className="underline hover:text-gray-300"
              >
                DEBSEXPRESSIONS@GMAIL.COM
              </a>
            </p>
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Deb's Expressions. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
